import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AVATAR_MAX_ZOOM,
  AVATAR_MIN_ZOOM,
  BANNER_TARGET_BYTES,
  clampRectOffset,
  coverScaleRect,
  cropBannerToFile,
  formatBytes,
  getBannerCropViewSize,
  getBannerDisplayMetrics,
  getBannerOutputSize,
  loadImageFromFile,
} from "@/lib/cropAvatarImage";

/**
 * Cover crop whose frame matches the live profile banner aspect on this screen,
 * so framing lines up with object-cover on the profile.
 */
export default function BannerCropDialog({
  open,
  file,
  onOpenChange,
  onCropped,
}) {
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoom, setZoom] = useState(AVATAR_MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [view, setView] = useState(() => getBannerCropViewSize());
  const dragRef = useRef(null);
  const frameRef = useRef(null);

  const { viewW, viewH, aspect } = view;

  const refreshViewSize = useCallback(() => {
    const metrics = getBannerDisplayMetrics();
    // Fit preview to dialog content width when available.
    const frameW = frameRef.current?.clientWidth;
    const maxW = frameW && frameW > 80 ? frameW : undefined;
    setView(getBannerCropViewSize(metrics.aspect, maxW));
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    refreshViewSize();
    const onResize = () => refreshViewSize();
    window.addEventListener("resize", onResize);
    const mq = window.matchMedia("(min-width: 640px)");
    mq.addEventListener?.("change", onResize);

    const el = frameRef.current;
    let ro;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => refreshViewSize());
      ro.observe(el);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      mq.removeEventListener?.("change", onResize);
      ro?.disconnect();
    };
  }, [open, refreshViewSize]);

  useEffect(() => {
    if (!open || !file) {
      setImage(null);
      setPreviewUrl(null);
      setZoom(AVATAR_MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
      setError(null);
      setLoadError(null);
      setBusy(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    setLoadError(null);
    loadImageFromFile(file)
      .then(({ image: img, objectUrl: url }) => {
        objectUrl = url;
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setImage(img);
        setPreviewUrl(url);
        setZoom(AVATAR_MIN_ZOOM);
        setOffset({ x: 0, y: 0 });
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.message || "Could not load image");
          setImage(null);
          setPreviewUrl(null);
        }
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, file]);

  // Re-clamp pan when the live aspect / preview size changes.
  useEffect(() => {
    if (!image) return;
    const clamped = clampRectOffset(
      image.naturalWidth,
      image.naturalHeight,
      viewW,
      viewH,
      zoom,
      offset.x,
      offset.y
    );
    if (clamped.offsetX !== offset.x || clamped.offsetY !== offset.y) {
      setOffset({ x: clamped.offsetX, y: clamped.offsetY });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when view/image/zoom change
  }, [image, viewW, viewH, zoom]);

  const setClampedOffset = useCallback(
    (x, y, nextZoom = zoom) => {
      if (!image) return;
      const clamped = clampRectOffset(
        image.naturalWidth,
        image.naturalHeight,
        viewW,
        viewH,
        nextZoom,
        x,
        y
      );
      setOffset({ x: clamped.offsetX, y: clamped.offsetY });
    },
    [image, viewW, viewH, zoom]
  );

  const onZoomChange = (value) => {
    const nextZoom = Number(value);
    setZoom(nextZoom);
    setClampedOffset(offset.x, offset.y, nextZoom);
  };

  const onPointerDown = (e) => {
    if (!image || busy) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    setClampedOffset(drag.originX + dx, drag.originY + dy);
  };

  const onPointerUp = (e) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
    }
  };

  const onWheel = (e) => {
    if (!image || busy) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    const nextZoom = Math.min(
      AVATAR_MAX_ZOOM,
      Math.max(AVATAR_MIN_ZOOM, zoom + delta)
    );
    setZoom(nextZoom);
    setClampedOffset(offset.x, offset.y, nextZoom);
  };

  const handleApply = async () => {
    if (!image || !file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { outputW, outputH } = getBannerOutputSize(aspect);
      const cropped = await cropBannerToFile({
        image,
        viewW,
        viewH,
        zoom,
        offsetX: offset.x,
        offsetY: offset.y,
        outputW,
        outputH,
        maxBytes: BANNER_TARGET_BYTES,
        fileName: file.name || "banner.jpg",
      });
      await onCropped?.(cropped);
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || "Could not crop image");
    } finally {
      setBusy(false);
    }
  };

  const scale = image
    ? coverScaleRect(image.naturalWidth, image.naturalHeight, viewW, viewH) *
      zoom
    : 1;
  const drawnW = image ? image.naturalWidth * scale : 0;
  const drawnH = image ? image.naturalHeight * scale : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Adjust cover photo</DialogTitle>
          <DialogDescription>
            This frame matches your cover on this screen. Drag to reposition and
            zoom to fill.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-stretch gap-4" ref={frameRef}>
          <div
            className="relative mx-auto touch-none select-none overflow-hidden bg-muted shadow-inner cursor-grab active:cursor-grabbing ring-1 ring-border"
            style={{ width: viewW, height: viewH }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            role="presentation"
          >
            {image && previewUrl ? (
              <img
                src={previewUrl}
                alt=""
                draggable={false}
                className="pointer-events-none absolute max-w-none"
                style={{
                  width: drawnW,
                  height: drawnH,
                  left: viewW / 2 + offset.x - drawnW / 2,
                  top: viewH / 2 + offset.y - drawnH / 2,
                }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                {loadError || "Loading…"}
              </div>
            )}
          </div>

          <div className="w-full space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="banner-zoom">Zoom</Label>
              <span className="text-xs text-muted-foreground tabular-nums">
                {zoom.toFixed(2)}×
              </span>
            </div>
            <input
              id="banner-zoom"
              type="range"
              min={AVATAR_MIN_ZOOM}
              max={AVATAR_MAX_ZOOM}
              step={0.01}
              value={zoom}
              disabled={!image || busy}
              onChange={(e) => onZoomChange(e.target.value)}
              className="w-full accent-primary"
            />
          </div>

          {file ? (
            <p className="text-xs text-muted-foreground w-full">
              Original {formatBytes(file.size)}
              {file.size > BANNER_TARGET_BYTES
                ? " — will be compressed on apply"
                : ""}
            </p>
          ) : null}

          {(error || loadError) && (
            <p className="text-sm text-destructive w-full">{error || loadError}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => onOpenChange?.(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!image || busy || !!loadError}
            onClick={handleApply}
          >
            {busy ? "Compressing…" : "Use cover"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
